import React, {useEffect} from "react";
import {Box, CollectionPreferencesProps} from "@amzn/awsui-components-react";
import {
    NO_DATA_MESSAGE, NO_MATCH_MESSAGE
} from "src/components/ic-account-mapping/constants";
import {
    PropertyFilterOperator,
    PropertyFilterOperatorExtended,
    PropertyFilterProperty,
    useCollection
} from "@amzn/awsui-collection-hooks";
import EmptyTableView from "src/components/ic-account-mapping/shared/EmptyTableView";
import {formatDateTime} from "src/components/ic-account-mapping/Utils";
import BaseTableView from "src/components/ic-account-mapping/shared/BaseTableView";
import {TableProps} from "@amzn/awsui-components-react/polaris/table";
import {DateTimeForm} from "src/components/ic-account-mapping/shared/DateTimeForm";
import {
    ActivityLogRecord
} from "src/models/reports/GetAccountMapping";
import ServiceCollection from "src/services/ServiceCollection";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";

const ColumnIdToColumnNameMapping = {
    accountId: "Account ID",
    updateFor: "Item",
    updateType: "Event",
    oldMapping: "Old value",
    newMapping: "New value",
    updatedBy: "Performed by",
    timestamp: "Timestamp",
}

function prepareColumnDefinition(): TableProps.ColumnDefinition<any>[] {
    const columnDefinitions: TableProps.ColumnDefinition<any>[] = [];
    Object.entries(ColumnIdToColumnNameMapping).forEach(([columnId, columnName]: [string, string]) => {
        columnDefinitions.push({
            id: columnId,
            header: columnName,
            cell: values => {
                if (columnName === ColumnIdToColumnNameMapping.timestamp) {
                    return values[columnId] ? values[columnId].toISOString() : "";
                } else {
                    return values[columnId];
                }
            },
            isRowHeader: true,
            sortingField: columnId,
        });
    });
    return columnDefinitions;
}

// These custom operators are based on UX requirements
function getFilteringOperators(columnName: string): (PropertyFilterOperator | PropertyFilterOperatorExtended<any>)[] {
    switch (columnName) {
        case ColumnIdToColumnNameMapping.accountId:
        case ColumnIdToColumnNameMapping.updateFor:
            return ["=", ":"];
        case ColumnIdToColumnNameMapping.updatedBy:
            return ["="];
        case ColumnIdToColumnNameMapping.timestamp:
            return [">=", "<="].map((operator) => {
                return {
                    operator: operator,
                    match: "datetime",
                    form: DateTimeForm,
                    format: formatDateTime,
                }
            });
        default:
            return ["=", "!="];
    }
}

export default function ActivityLogSectionView(props: { services: ServiceCollection }) {
    const [preferences, setPreferences] = React.useState<CollectionPreferencesProps.Preferences>({
        pageSize: 10,
    });
    const [accountMappingActivityLogs, setAccountMappingActivityLogs] = React.useState<ActivityLogRecord[]>([]);
    const [getAccountMappingActivityLogsResponse, getAccountMappingActivityLogsLoading, getAccountMappingActivityLogsError] =
        props.services.reportsService.getAccountMappingActivityLogs();

    useEffect(() => {
        if (getAccountMappingActivityLogsResponse) {
            setAccountMappingActivityLogs(getAccountMappingActivityLogsResponse.activityLogs.map((activityLog: ActivityLogRecord) => {
                return {
                    ...activityLog,
                    oldMapping: activityLog.oldMapping ?? "-",
                    timestamp: activityLog.timestamp ? new Date(activityLog.timestamp) : "-"
                }
            }));
        }
    }, [getAccountMappingActivityLogsResponse]);

    useEffect(() => {
        if (getAccountMappingActivityLogsError) {
            props.services.messageService.showErrorAutoDismissBanner(getAccountMappingActivityLogsError);
        }
    }, [getAccountMappingActivityLogsError]);

    const useCollectionResult = useCollection(
        accountMappingActivityLogs,
        {
            pagination: {
                pageSize: preferences.pageSize
            },
            propertyFiltering: {
                empty: <EmptyTableView message={NO_DATA_MESSAGE}/>,
                noMatch: <EmptyTableView message={NO_MATCH_MESSAGE}/>,
                defaultQuery: {
                    tokens: [],
                    operation: "or"
                },
                filteringProperties: (Object.entries(ColumnIdToColumnNameMapping).map(([columnId, columnName]: [string, string]): PropertyFilterProperty => ({
                    key: columnId,
                    propertyLabel: columnName,
                    groupValuesLabel: `${columnName} Values`,
                    operators: getFilteringOperators(columnName),
                    // Defining defaultOperator is required to remove "=" from operators list for timestamp column
                    defaultOperator: columnName === ColumnIdToColumnNameMapping.timestamp ? "<=" : "="
                })))
            }
        }
    )

    return (
        <>
            {
                getAccountMappingActivityLogsLoading ? (
                        <Box textAlign="center">
                            <Spinner size="large"/>
                        </Box>
                    ) :
                    <BaseTableView
                        header={"Activity Log"}
                        description={"Find history of change on account mapping"}
                        columnDefinitions={prepareColumnDefinition()}
                        preferences={preferences}
                        setPreferences={setPreferences}
                        useCollectionResult={useCollectionResult}
                    />
            }
        </>
    );
}